import { useState } from "react";
import { Calendar, Check, Loader2, Lock } from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { startGoogleConnect, completeGoogleConnect } from "@/lib/google-connect.functions";
import { runGoogleConnect } from "@/lib/google-connect-popup";
import { useGoogleConnections } from "@/components/google-connection-row";
import { getProfile } from "@/lib/relationships.functions";
import { canUseLiveContext } from "@/lib/plans";
import { openUpgrade } from "@/lib/upgrade-store";

export function OnboardingCalendarConnect() {
  const start = useServerFn(startGoogleConnect);
  const complete = useServerFn(completeGoogleConnect);
  const fetchProfile = useServerFn(getProfile);
  const qc = useQueryClient();
  const { data: connections } = useGoogleConnections();
  const { data: profile } = useQuery({ queryKey: ["profile"], queryFn: () => fetchProfile() });
  const [busy, setBusy] = useState(false);

  const connected = !!connections?.google_calendar?.connected;
  const available = connections?.google_calendar?.available !== false;
  const allowed = canUseLiveContext((profile as any)?.plan);

  if (connected) {
    return (
      <div className="flex w-full items-center justify-center gap-2 rounded-2xl glass-soft px-5 py-4 text-sm font-medium text-emerald-400">
        <Check className="h-4 w-4" /> Live Context is on
      </div>
    );
  }

  if (!allowed) {
    return (
      <button
        type="button"
        onClick={() => openUpgrade("live-context")}
        className="flex w-full items-center justify-center gap-2 rounded-2xl glass-soft px-5 py-4 text-sm font-medium text-foreground hover:bg-white/[0.04]"
      >
        <Lock className="h-4 w-4 text-[var(--accent-neon-ink)]" strokeWidth={1.75} />
        Live Context is a Pro feature — see Pro
      </button>
    );
  }

  return (
    <div className="space-y-2">
      <button
        onClick={async () => {
          if (!available) {
            toast("Calendar sync is rolling out soon — we'll email you when it's live.");
            return;
          }
          setBusy(true);
          try {
            await runGoogleConnect("google_calendar", (args) => start(args), (args) => complete(args));
            toast.success("Calendar connected");
            qc.invalidateQueries({ queryKey: ["google-connections"] });
          } catch (e: any) {
            toast.error(e?.message ?? "Couldn't connect");
          } finally {
            setBusy(false);
          }
        }}
        disabled={busy}
        className="flex w-full items-center justify-center gap-2 rounded-2xl glass-soft px-5 py-4 text-sm font-medium text-foreground hover:bg-white/[0.04] disabled:opacity-60"
      >
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Calendar className="h-4 w-4" />}
        {busy ? "Connecting…" : "Turn on Live Context"}
      </button>
      <p className="text-center text-xs text-muted-foreground/80">
        Optional — connect later from Settings anytime.
      </p>
    </div>
  );
}
